import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import * as yup from 'yup'
import AnimateHeight from '../../components/AnimateHeight'
import FormButton from '../../components/FormButton'
import FormCheckbox from '../../components/FormCheckbox'
import FormInput from '../../components/FormInput'
import MessageBox from '../../components/MessageBox'
import PenningWebsiteHeaderLink from '../../components/PenningWebsiteHeaderLink'
import {
  useCreateAccountMutation,
  useResendVeliActivationLinkMutation,
} from '../../redux/services/coreApi'
import type { Role } from '../../types/coreApi-types'
import { attemptAutoLoginAfterSignup } from '../../utils/autoLoginAfterSignup'
import {
  getPenningVisitorId,
  getTrackingPayload,
  persistTrackingParams,
} from '../../utils/trackingParams'
import AccountTypeForm from './components/AccountTypeForm'
import Form from './components/Form'
import passwordValidator from './utils/passwordValidator'

type SignUpScreenProps = {}

type FormData = {
  firstName?: string
  lastName?: string
  companyName?: string
  phone: string
  email: string
  password: string
  confirmPassword: string
  vatNumber?: string
  referralCode?: string
  newsletter?: boolean
  termsOfService: boolean
}

/** Map URL params + persisted tracking to signupParameters object (Record<string, string>) */
function useSignupParameters(): Record<string, string> {
  const location = useLocation()
  return useMemo(() => {
    const { params, landingPage, referrer } = getTrackingPayload(
      location.search,
      location.pathname
    )
    const result: Record<string, string> = {}
    if (landingPage && landingPage !== '') result.landingPage = landingPage
    if (referrer && referrer !== '') result.referrer = referrer
    const map: Record<string, string> = {
      utm_source: 'utmSource',
      utm_medium: 'utmMedium',
      utm_campaign: 'utmCampaign',
      utm_content: 'utmContent',
      utm_term: 'utmTerm',
      gclid: 'gclid',
      gbraid: 'gbraid',
      wbraid: 'wbraid',
      _pn_vid: 'visitor_id',
    }
    for (const [key, value] of Object.entries(params)) {
      if (value == null || value === '') continue
      const camel = map[key] ?? key
      result[camel] = value
    }
    // visitor_id from cookie when not in URL (same pattern as other params)
    if (!result.visitor_id) {
      const fromCookie = getPenningVisitorId(location.search)
      if (fromCookie && fromCookie !== '') result.visitor_id = fromCookie
    }
    return result
  }, [location.search, location.pathname])
}

const SignUpScreen: React.FC<SignUpScreenProps> = () => {
  const history = useHistory()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const referralCode = searchParams.get('referralCode')
  const signupParameters = useSignupParameters()

  // Persist params when landing on /sign-up with query (e.g. direct link) so we keep first-touch
  useEffect(() => {
    persistTrackingParams(location.search, location.pathname)
  }, [location.search, location.pathname])

  const [page, setPage] = useState<'account-type' | 'account-info'>(
    'account-type'
  )
  const [error, setError] = useState<string | null>(null)
  const [role, setRole] = useState<Role | null>(null)
  // A transferring Veli client who wandered into normal signup is blocked with the
  // `VeliClientMustActivate` code (their account is created via the branded activation link, not plain
  // signup). When that happens we offer to re-send that link to their mailbox, right in the error box.
  const [isVeliActivationError, setIsVeliActivationError] = useState(false)
  const [activationLinkSent, setActivationLinkSent] = useState(false)

  const [createAccount, { isLoading: isCreatingAccount }] =
    useCreateAccountMutation()
  const [resendVeliActivationLink, { isLoading: isResendingActivationLink }] =
    useResendVeliActivationLinkMutation()

  const onSubmit: SubmitHandler<FormData> = async ({
    firstName,
    lastName,
    companyName,
    phone,
    email,
    password,
    vatNumber,
    referralCode,
    newsletter,
  }) => {
    const isOrganization = role === 'Organization'
    if (!role) return

    try {
      await createAccount({
        firstName,
        lastName,
        companyName,
        phone,
        email,
        password,
        role,
        vatNumber: isOrganization ? vatNumber : undefined,
        referralCode: referralCode === '' ? undefined : referralCode,
        newsletterAccepted: Boolean(newsletter),
        signupParameters:
          Object.keys(signupParameters).length > 0
            ? signupParameters
            : undefined,
      }).unwrap()
      // Try to log the user straight in with the credentials they just set,
      // skipping a manual return trip through Universal Login. Falls back to the
      // standard login redirect (autoLoggedIn=false) if auto-login isn't possible.
      const autoLoggedIn = await attemptAutoLoginAfterSignup(email, password)
      history.replace('/signup-complete', { autoLoggedIn })
    } catch (err) {
      const data = (err as { data?: { errors?: unknown; code?: string } })?.data
      setError(
        typeof data?.errors === 'string'
          ? data.errors
          : 'Something went wrong. Please try again.'
      )
      setIsVeliActivationError(data?.code === 'VeliClientMustActivate')
      setActivationLinkSent(false)
    }
  }

  // Re-send the branded activation link to the email the client typed. The endpoint is non-committal
  // (it never says whether the address matched), so we optimistically show the same confirmation
  // regardless of the outcome — the link only ever reaches the mailbox.
  const onRequestNewActivationLink = async () => {
    try {
      await resendVeliActivationLink(watch('email')).unwrap()
    } catch {
      // Swallow — the endpoint is intentionally non-committal; the client is told to check their inbox.
    }
    setActivationLinkSent(true)
  }

  const schema: yup.SchemaOf<FormData> = yup.object({
    firstName: yup.string().when([], {
      is: () => role === 'Individual',
      then: yup.string().required('This field is required.'),
    }),
    lastName: yup.string().when([], {
      is: () => role === 'Individual',
      then: yup.string().required('This field is required.'),
    }),
    companyName: yup.string().when([], {
      is: () => role === 'Organization',
      then: yup.string().required('This field is required.'),
    }),
    phone: yup.string().required('This field is required.'),
    email: yup
      .string()
      .email('Please enter a valid email.')
      .required('This field is required.'),
    password: passwordValidator().required('This field is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords must match.')
      .required('This field is required.'),
    vatNumber: yup.string().when([], {
      is: () => role === 'Organization',
      then: yup.string().required('This field is required'),
    }),
    referralCode: yup.string(),
    newsletter: yup.bool(),
    termsOfService: yup
      .boolean()
      .required('The terms of service must be accepted.')
      .oneOf([true], 'The terms of service must be accepted.'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    if (referralCode) {
      setValue('referralCode', referralCode)
    }
  }, [referralCode])

  return (
    <div className="bg-bg-base dark:bg-brand-dark-background overflow-scroll min-h-screen flex flex-col justify-center items-center p-3 ">
      <PenningWebsiteHeaderLink />
      {page === 'account-type' && (
        <AccountTypeForm
          onClickContinue={role => {
            setRole(role)
            setPage('account-info')
          }}
        />
      )}

      {page === 'account-info' && (
        <div className="pt-40 pb-20">
          <Form
            direction="fromRight"
            title="Account info"
            onPressBack={() => {
              setPage('account-type')
              setError(null)
              setIsVeliActivationError(false)
              setActivationLinkSent(false)
            }}
            backTitle="Back"
            isLoading={isCreatingAccount}
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-7">
              <div className="space-y-3">
                {role === 'Organization' && (
                  <div className="space-y-3">
                    <FormInput
                      label="CVR / VAT number"
                      inputMode="text"
                      registeredForm={register('vatNumber')}
                      error={errors?.vatNumber}
                    />
                    <FormInput
                      label="Company name"
                      inputMode="text"
                      registeredForm={register('companyName')}
                      error={errors?.companyName}
                    />
                  </div>
                )}

                {role === 'Individual' && (
                  <div className="space-y-3">
                    <FormInput
                      label="First name"
                      inputMode="text"
                      registeredForm={register('firstName')}
                      error={errors?.firstName}
                    />
                    <FormInput
                      label="Last name"
                      inputMode="text"
                      registeredForm={register('lastName')}
                      error={errors?.lastName}
                    />
                  </div>
                )}

                <FormInput
                  label="Phone"
                  inputMode="text"
                  registeredForm={register('phone')}
                  error={errors?.phone}
                />
                <FormInput
                  label="Email"
                  inputMode="email"
                  registeredForm={register('email')}
                  error={errors?.email}
                />

                <FormInput
                  label="Password"
                  inputMode="text"
                  isSecure
                  registeredForm={register('password')}
                  error={errors?.password}
                  showPassword
                />
                <FormInput
                  label="Confirm password"
                  inputMode="text"
                  isSecure
                  registeredForm={register('confirmPassword')}
                  error={errors?.confirmPassword}
                  showPassword
                />
                <FormInput
                  label="Referral code (Optional)"
                  inputMode="text"
                  registeredForm={register('referralCode')}
                  error={errors?.referralCode}
                />
              </div>
              <div className="space-y-3">
                <FormCheckbox
                  id="newsletter-checkbox"
                  renderLabel={
                    <>
                      I want to receive emails about news and weekly updates on
                      the market.
                    </>
                  }
                  registeredForm={register('newsletter')}
                  error={errors?.newsletter}
                />

                <FormCheckbox
                  id="tos"
                  renderLabel={
                    <>
                      I agree to the{' '}
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://www.penning.io/terms-conditions"
                        className="hover:underline text-bg-accent dark:text-white"
                      >
                        terms of service.
                      </a>
                    </>
                  }
                  registeredForm={register('termsOfService')}
                  error={errors?.termsOfService}
                />
              </div>
              <AnimateHeight isVisible={error !== null}>
                <MessageBox title={error ?? ''} type="warning">
                  {isVeliActivationError &&
                    (activationLinkSent ? (
                      <p className="text-xs font-semi text-brand-light-red dark:text-white">
                        A new activation link is on its way. Please check your
                        inbox (and your spam folder).
                      </p>
                    ) : (
                      <button
                        type="button"
                        disabled={isResendingActivationLink}
                        onClick={onRequestNewActivationLink}
                        className="text-xs font-semi underline text-brand-light-red dark:text-white disabled:opacity-50"
                      >
                        {isResendingActivationLink
                          ? 'Sending…'
                          : 'Request new link'}
                      </button>
                    ))}
                </MessageBox>
              </AnimateHeight>
              <FormButton
                isLoading={false}
                title="Create account"
                loadingText="Creating your account"
              />
            </form>
          </Form>
        </div>
      )}
    </div>
  )
}

export default SignUpScreen
